import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TemplateCard = ({ template, onPreview, onUseTemplate, onToggleFavorite, hasActiveSubscription }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div
      className="group relative bg-card border border-border rounded-xl overflow-hidden transition-all duration-250 hover:shadow-lg hover:border-primary/30"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <Image
          src={template?.preview}
          alt={template?.previewAlt}
          className={`w-full h-full object-cover transition-all duration-500 ${
            isHovered ? 'scale-105' : 'scale-100'
          } ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
        />
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon name="Image" size={48} className="text-muted-foreground animate-pulse" />
          </div>
        )}
        
        {template?.featured && (
          <div className="absolute top-3 left-3 bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
            <Icon name="Star" size={12} />
            <span>Featured</span>
          </div>
        )}

        {hasActiveSubscription && (
          <div className="absolute top-3 left-3 bg-success text-success-foreground px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
            <Icon name="Check" size={12} />
            <span>Included</span>
          </div>
        )}

        <button
          onClick={() => onToggleFavorite(template?.id)}
          className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-250 ${
            template?.isFavorite
              ? 'bg-error text-error-foreground'
              : 'bg-background/80 backdrop-blur-sm text-foreground hover:bg-background'
          }`}
          aria-label={template?.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          <Icon name="Heart" size={16} className={template?.isFavorite ? 'fill-current' : ''} />
        </button>

        <div
          className={`absolute inset-0 bg-gradient-to-t from-background/95 via-background/50 to-transparent flex items-end justify-center pb-4 gap-2 transition-all duration-250 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <Button
            variant="secondary"
            size="sm"
            iconName="Eye"
            iconPosition="left"
            onClick={() => onPreview(template)}
          >
            Preview
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName={hasActiveSubscription ? "Rocket" : "Lock"}
            iconPosition="left"
            onClick={() => onUseTemplate(template)}
          >
            {hasActiveSubscription ? "Use Template" : "Subscribe to Use"}
          </Button>
        </div>
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-heading font-semibold text-base md:text-lg text-foreground line-clamp-1">
            {template?.name}
          </h3>
          <div className="flex items-center gap-1 text-accent flex-shrink-0">
            <Icon name="Star" size={14} className="fill-current" />
            <span className="text-sm font-medium">{template?.rating}</span>
          </div>
        </div>

        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {template?.description}
        </p>

        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
            <Icon name="Tag" size={12} />
            {template?.category}
          </span>
          <div className="flex items-center gap-1 text-muted-foreground text-xs">
            <Icon name="Download" size={12} />
            <span>{template?.downloads}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {template?.features?.slice(0, 3)?.map((feature, index) => (
            <span
              key={index}
              className="px-2 py-0.5 bg-muted text-muted-foreground rounded text-xs"
            >
              {feature}
            </span>
          ))}
          {template?.features?.length > 3 && (
            <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded text-xs">
              +{template?.features?.length - 3}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateCard;